import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const feedbackTypes = ['产品建议', '功能问题', '兑换问题', '物流问题', '其他'];

const FeedbackPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState('');
  const [content, setContent] = useState('');
  const [contact, setContact] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!selectedType || !content.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const { error: insertError } = await supabase.from('feedbacks').insert({
        user_id: user?.phone ?? null,
        phone: user?.phone ?? null,
        feedback_type: selectedType,
        content: content.trim(),
        contact: contact.trim() || null,
        status: 'pending',
      });
      if (insertError) throw insertError;
      setSubmitted(true);
    } catch (e: unknown) {
      setError('提交失败，请稍后重试');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center max-w-[480px] mx-auto px-8">
        <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
          <i className="ri-check-line text-emerald-500 text-3xl" />
        </div>
        <p className="font-bold text-gray-900 text-lg mb-2">反馈已提交！</p>
        <p className="text-sm text-gray-500 text-center mb-6">感谢您的宝贵意见，我们会在1-3个工作日内处理并回复</p>
        <button onClick={() => navigate(-1)} className="w-full bg-emerald-500 text-white py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
          返回
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center px-4 h-14 gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-gray-700 text-xl" />
          </button>
          <h1 className="font-bold text-gray-900 text-base">留言反馈</h1>
        </div>
      </header>

      <div className="px-4 pt-4 space-y-3">
        {/* Type */}
        <div className="bg-white rounded-2xl p-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">反馈类型 <span className="text-rose-500">*</span></p>
          <div className="flex flex-wrap gap-2">
            {feedbackTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-2 rounded-full text-sm border transition-all cursor-pointer whitespace-nowrap ${
                  selectedType === type ? 'border-emerald-500 bg-emerald-50 text-emerald-600 font-medium' : 'border-gray-200 text-gray-600'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl p-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">反馈内容 <span className="text-rose-500">*</span></p>
          <textarea
            placeholder="请详细描述您的问题或建议，我们会认真对待每一条反馈..."
            value={content}
            onChange={(e) => { if (e.target.value.length <= 500) setContent(e.target.value); }}
            rows={6}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-300 resize-none"
          />
          <p className="text-xs text-gray-400 text-right mt-1">{content.length}/500</p>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl p-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">联系方式（选填）</p>
          <input
            type="text"
            placeholder="手机号或微信号，方便我们联系您"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-emerald-300"
          />
        </div>

        {/* Tips */}
        <div className="bg-emerald-50 rounded-2xl p-4">
          <div className="flex items-start gap-2">
            <i className="ri-information-line text-emerald-600 text-base mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-emerald-700 mb-1">温馨提示</p>
              <p className="text-xs text-emerald-600 leading-relaxed">我们会在1-3个工作日内处理您的反馈。如有紧急问题，请拨打客服热线：400-888-9999</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 rounded-xl px-4 py-3 text-sm text-rose-500 text-center">{error}</div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!selectedType || !content.trim() || submitting}
          className={`w-full py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
            selectedType && content.trim() ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
          }`}
        >
          {submitting ? '提交中...' : '提交反馈'}
        </button>
      </div>
    </div>
  );
};

export default FeedbackPage;
